import React, { useEffect, useState } from 'react';
import {
  personalInformationInitialValues,
  personalInformationValidationSchema,
  PersonalInformationSchemaType,
} from '../../forms/personalInformation'
import {
  credentialsInitialValues,
  CredentialsSchemaType,
  credentialsValidationSchema,
} from '../../forms/credentials'
import api from '../../api/api';
import useApi from '../../hooks/use-api';
import { useTranslation } from 'react-i18next';
import { useFormik } from 'formik'
import Input from '../../components/Input'
import Sidebar from '../../components/Sidebar/Sidebar'
import { SidebarItemProps } from '../../components/Sidebar/SidebarItem'
import AccountTopBar from '../../components/AccountTopBar/AccountTopBar'
import { Tags, Tag, UserType } from '../../utils/props';

export default function PersonalInformationPage() {
  const { t } = useTranslation('ns');
  const [userProfileTag, setUserProfileTag] = useState<number | undefined>(undefined);
  const [showSuccess, setShowSuccess] = useState(false);
  const [showError, setShowError] = useState(false);
  const [showCredentialsSuccess, setShowCredentialsSuccess] = useState(false);
  const [showCredentialsError, setShowCredentialsError] = useState(false);
  const [credentialsErrorMessage, setCredentialsErrorMessage] = useState('Failed to update login information. Please try again.');

  const getCustomUrl = () => {
    const customUrl = (localStorage.getItem('custom_url') ?? '').replace(/['"]+/g, '').trim()
    if (!customUrl) {
      return ''
    }

    return /^https?:\/\//i.test(customUrl) ? customUrl : `https://${customUrl}`
  }

  const getUser = useApi({
    path: '/user',
    callback: (data) => {
      localStorage.setItem('user_id', JSON.stringify(data.id));
      localStorage.setItem('first_name', JSON.stringify(data.first_name));
      localStorage.setItem('last_name', JSON.stringify(data.last_name));
      localStorage.setItem('company_name', JSON.stringify(data.company_name));
      localStorage.setItem('phone', JSON.stringify(data.phone));
      localStorage.setItem('mail', JSON.stringify(data.mail));
      localStorage.setItem('type', JSON.stringify(data.type));
      localStorage.setItem('custom_url', JSON.stringify(data.custom_url ?? ''));
      personalInformationForm.setFieldValue('firstName', data.first_name ?? '')
      personalInformationForm.setFieldValue('lastName', data.last_name ?? '')
      personalInformationForm.setFieldValue('companyName', data.company_name ?? '')
      personalInformationForm.setFieldValue('fileId', data.file_id ?? '')
      personalInformationForm.setFieldValue('phone', data.phone ?? '')
      credentialsForm.setFieldValue('mail', data.mail)
    }
  })

  useEffect(() => {
    getUser.get();
    tags.get();
  }, [])

  const tags = useApi({
    path: '/tags',
    callback: (data) => {
      const profileTag = data.items.find((item: Tag) => item.name === String(Tags.ProfilePicture));
      setUserProfileTag(profileTag?.id);
    }
  })

  const sidebarItems: SidebarItemProps[] = [
    {
      label: 'Personal Information',
      href: '#personal-information',
    },

    {
      label: 'Login Information',
      href: '#login-information',
    },
  ]

  const personalInformationForm = useFormik({
    initialValues: {
      firstName: '',
      lastName: '',
      companyName: '',
      phone: '',
      fileId: '',
      authToken: '',
    },
    validationSchema: personalInformationValidationSchema,
    onSubmit: (values: PersonalInformationSchemaType) => {
      onPersonalInformationUpdate(values)
    },
  })

  const { errors, touched, getFieldProps, handleSubmit } = personalInformationForm

  const onPersonalInformationUpdate = async (values: PersonalInformationSchemaType) => {
    const { firstName, lastName, phone, fileId, authToken, companyName } = values;
    const currentUserId = (localStorage.getItem('user_id') ?? '').replace(/['"]+/g, '')
    const currentUserType = (localStorage.getItem('type') ?? '').replace(/['"]+/g, '')
    const storedToken: string | null = localStorage.getItem('token');
    const headers = storedToken ? { Authorization: `Bearer ${JSON.parse(storedToken)}` } : {};
    const updatePath = currentUserType === UserType.Admin.toString()
      ? `/admin/${currentUserId}`
      : `/company/${currentUserId}`
    const payload: any = {
      first_name: firstName,
      last_name: lastName,
      file_id: fileId,
      phone: phone,
      auth_token: authToken,
    };
    if (localStorage.getItem('type') !== UserType.Admin.toString()) {
      payload.company_name = companyName;
    }
    try {
      const response = await api.put(updatePath, payload, { headers })
      const data = response.data

      localStorage.setItem('first_name', JSON.stringify(data.first_name ?? firstName));
      localStorage.setItem('last_name', JSON.stringify(data.last_name ?? lastName));
      localStorage.setItem('company_name', JSON.stringify(data.company_name ?? companyName));
      localStorage.setItem('phone', JSON.stringify(data.phone ?? phone));
      localStorage.setItem('mail', JSON.stringify(data.mail ?? (localStorage.getItem('mail') ?? '').replace(/['"]+/g, '')));
      localStorage.setItem('custom_url', JSON.stringify(data.custom_url ?? (localStorage.getItem('custom_url') ?? '').replace(/['"]+/g, '')));

      setShowSuccess(true);
      setTimeout(() => setShowSuccess(false), 3000);
    } catch (error) {
      setShowError(true);
      setTimeout(() => setShowError(false), 3000);
    }
  }

  const credentialsForm = useFormik({
    initialValues: credentialsInitialValues,
    validationSchema: credentialsValidationSchema,
    onSubmit: (values: CredentialsSchemaType) => {
      onCredentialsUpdate(values)
    },
  })

  const onCredentialsUpdate = async (values: CredentialsSchemaType) => {
    const { mail, password, newPassword, confirmNewPassword } = values
    const currentMail = (localStorage.getItem('mail') ?? '').replace(/['"]+/g, '')
    const currentUserId = (localStorage.getItem('user_id') ?? '').replace(/['"]+/g, '')
    const currentUserType = (localStorage.getItem('type') ?? '').replace(/['"]+/g, '')
    const isMailChanged = mail !== currentMail
    const isPasswordChangeRequested = Boolean(password || newPassword || confirmNewPassword)

    if (isPasswordChangeRequested && (!password || !newPassword)) {
      if (!password) {
        credentialsForm.setFieldError('password', 'This field is required')
      }
      if (!newPassword) {
        credentialsForm.setFieldError('newPassword', 'This field is required')
      }
      return
    }

    try {
      setShowCredentialsError(false)
      setCredentialsErrorMessage('Failed to update login information. Please try again.')

      const storedToken: string | null = localStorage.getItem('token');
      const headers = storedToken ? { Authorization: `Bearer ${JSON.parse(storedToken)}` } : {};

      if (isMailChanged) {
        const updatePath = currentUserType === UserType.Admin.toString()
          ? `/admin/${currentUserId}`
          : `/company/${currentUserId}`

        await api.put(updatePath, {
          mail,
        }, { headers })
      }

      if (isPasswordChangeRequested) {
        await api.post('/company/change_password', {
          old_password: password,
          new_password: newPassword,
        }, { headers })

        credentialsForm.setFieldValue('password', '')
        credentialsForm.setFieldValue('newPassword', '')
        credentialsForm.setFieldValue('confirmNewPassword', '')
      }

      if (isMailChanged || isPasswordChangeRequested) {
        setShowCredentialsSuccess(true)
        setTimeout(() => setShowCredentialsSuccess(false), 3000)
      }
    } catch (error) {
      const apiErrorMessage = (error as any)?.response?.data?.error || (error as any)?.response?.data?.message
      if (apiErrorMessage) {
        setCredentialsErrorMessage(apiErrorMessage)
        if (isMailChanged) {
          credentialsForm.setFieldError('mail', apiErrorMessage)
        }
      }
      setShowCredentialsError(true)
      setTimeout(() => setShowCredentialsError(false), 3000)
    }
  }

  return (
    <>
      <AccountTopBar />
      <div className="pd:mx-0 min-h-screen w-full bg-white px-5 md:flex lg:px-[190px]">
      <div className="flex-shrink-0 bg-white md:min-w-[260px]">
        <Sidebar
          userName={`${localStorage.getItem('first_name')?.replace(/['"]+/g, '') ?? ''} ${localStorage.getItem('last_name')?.replace(/['"]+/g, '') ?? ''}`}
          items={sidebarItems}
          tagId={userProfileTag ?? 0}
        />
      </div>
      <div className="flex flex-1 flex-col items-start py-12 md:ml-16">
        <div className="w-full">
          <h2 id="personal-information" className="mb-1 text-2xl font-bold">
            {t('personalInformation')}
          </h2>
          <p className="mb-6 text-base text-gray-700">{t('updateInformationInfo')}</p>
          {showSuccess && (
            <div className="flex items-center gap-2 bg-green-100 px-4 py-3 rounded mb-4">
              <span>Information updated!</span>
            </div>
          )}
          {showError && (
            <div className="flex items-center gap-2 bg-red-100 px-4 py-3 rounded mb-4">
              <span>Failed to update information. Please try again.</span>
            </div>
          )}
          <form
            className="mb-12 grid grid-cols-2 gap-x-6 gap-y-4"
            onSubmit={personalInformationForm.handleSubmit}
          >
            <Input
              {...personalInformationForm.getFieldProps('firstName')}
              title={t('firstName') + ' *'}
              error={touched.firstName ? (errors.firstName ?? '') : ''}
            />
            <Input
              {...personalInformationForm.getFieldProps('lastName')}
              title={t('lastName') + ' *'}
              error={touched.lastName ? (errors.lastName ?? '') : ''}
            />
            {localStorage.getItem('type') !== UserType.Admin.toString() && (
              <Input
                className="col-span-2"
                {...personalInformationForm.getFieldProps('companyName')}
                title={t('companyName')}
                error={touched.companyName ? (errors.companyName ?? '') : ''}
              />
            )}
            <Input
              {...personalInformationForm.getFieldProps('phone')}
              title={t('phone') + ' *'}
              placeholder="+105733124124"
              error={touched.phone ? (errors.phone ?? '') : ''}
            />
            <Input
              {...personalInformationForm.getFieldProps('fileId')}
              title={t('fileId')}
              error={touched.fileId ? (errors.fileId ?? '') : ''}
            />
            <div className="col-span-2 flex items-center gap-2">
              <span className="text-sm text-gray-700">{t('accountLink')}</span>
              {getCustomUrl() ? (
                <a
                  href={getCustomUrl()}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-sm text-blue-600 hover:underline"
                >
                  {getCustomUrl()}
                </a>
              ) : (
                <span className="text-sm text-gray-500">Not yet set</span>
              )}
            </div>
            <div className="col-span-2 mt-2 flex justify-start">
              <button
                type="submit"
                className="group relative border-2 border-blue-600 bg-white px-8 py-3 font-bold uppercase tracking-wide text-blue-600 shadow-none transition hover:bg-blue-600 hover:text-white"
                style={{
                  transform: 'skew(-20deg)',
                  borderRadius: 0,
                }}
              >
                <span
                  className="block"
                  style={{
                    transform: 'skew(20deg)',
                  }}
                >
                  {t('updateInformation')}
                </span>
              </button>
            </div>
          </form>
          <h2 id="login-information" className="mb-1 text-2xl font-bold">
            {t('loginInformation')}
          </h2>
          <p className="mb-6 text-base text-gray-700">{t('loginInformationInfo')}</p>
          {showCredentialsSuccess && (
            <div className="flex items-center gap-2 bg-green-100 px-4 py-3 rounded mb-4">
              <span>Login information updated!</span>
            </div>
          )}
          {showCredentialsError && (
            <div className="flex items-center gap-2 bg-red-100 px-4 py-3 rounded mb-4">
              <span>{credentialsErrorMessage}</span>
            </div>
          )}
          <form
            className="mb-7.5 grid grid-cols-2 gap-x-6 gap-y-4"
            onSubmit={credentialsForm.handleSubmit}
          >
            <Input
              className="col-span-2"
              {...credentialsForm.getFieldProps('mail')}
              title={t('mail') + ' *'}
              error={credentialsForm.touched.mail ? (credentialsForm.errors.mail ?? '') : ''}
            />
            <Input
              className="col-span-2"
              {...credentialsForm.getFieldProps('password')}
              type="password"
              title={t('password') + ' *'}
              error={
                credentialsForm.touched.password ? (credentialsForm.errors.password ?? '') : ''
              }
            />
            <Input
              {...credentialsForm.getFieldProps('newPassword')}
              type="password"
              title={t('newPassword') + ' *'}
              error={
                credentialsForm.touched.newPassword
                  ? (credentialsForm.errors.newPassword ?? '')
                  : ''
              }
            />
            <Input
              {...credentialsForm.getFieldProps('confirmNewPassword')}
              type="password"
              title={t('confirmNewPassword') + ' *'}
              error={
                credentialsForm.touched.confirmNewPassword
                  ? (credentialsForm.errors.confirmNewPassword ?? '')
                  : ''
              }
            />
            <div className="col-span-2 mt-2 flex justify-start">
              <button
                type="submit"
                className="group relative border-2 border-blue-600 bg-white px-8 py-3 font-bold uppercase tracking-wide text-blue-600 shadow-none transition hover:bg-blue-600 hover:text-white"
                style={{
                  transform: 'skew(-20deg)',
                  borderRadius: 0,
                }}
              >
                <span
                  className="block"
                  style={{
                    transform: 'skew(20deg)',
                  }}
                >
                  {t('updateInformation')}
                </span>
              </button>
            </div>
          </form>
        </div>
      </div>
      </div>
    </>
  )

}